/**
 * The navigation of docs/07-gui-proposal §2, as data: one table read by the side nav and by the
 * route table, so a screen can never appear in one and not the other.
 *
 * `ready: false` marks a screen this stage has not built yet. It is still listed — the reader
 * sees the shape of the system and that the screen is coming (the approved mockup does the same
 * with its "đang dựng" tag) — but it is not a link to an empty page.
 *
 * Two things were added on 12/09/2026 with the full-height sidebar (docs/13-tasks/01-ui-chrome.md#18):
 *
 *  1. FIVE groups instead of two. Fifteen screens under two headings is a list, not a menu: the
 *     eye has to read every label to find one. Grouped by the QUESTION each screen answers
 *     (điều hành · phân tích · tra cứu · danh mục · thu thập · hệ thống), the reader picks a heading first
 *     and then reads four items, not fifteen.
 *  2. An `icon` NAME per item and per group — a name, not a component, because this file is the
 *     data table and has to stay importable by the route table without pulling any JSX in. The
 *     drawings live in `layout/NavIcons.tsx`; a guard checks every name here has one there.
 */
export type IconName =
  | 'gauge'
  | 'calendar'
  | 'compass'
  | 'ship'
  | 'pie'
  | 'bars'
  | 'trend'
  | 'download'
  | 'anchor'
  | 'layers'
  | 'box'
  | 'users'
  | 'shield'
  | 'account'
  | 'key'
  | 'search'
  | 'folder'
  | 'sliders';

export interface NavItem {
  readonly path: string;
  readonly label: string;
  /** Only `admin` may open it (docs/06-api-contract §1: /api/admin/* and the export). */
  readonly adminOnly?: boolean;
  readonly ready: boolean;
  readonly icon: IconName;
}

export interface NavSection {
  readonly title: string;
  readonly icon: IconName;
  readonly items: readonly NavItem[];
}

export const NAVIGATION: readonly NavSection[] = [
  {
    title: 'Điều hành',
    icon: 'gauge',
    items: [
      { path: '/', label: 'Tổng quan', ready: true, icon: 'gauge' },
      { path: '/movements', label: 'Lịch điều động', ready: true, icon: 'calendar' },
    ],
  },
  {
    title: 'Phân tích',
    icon: 'bars',
    items: [
      { path: '/share/terminals', label: 'Thị phần cảng', ready: true, icon: 'pie' },
      { path: '/share/agents', label: 'Thị phần đại lý', ready: true, icon: 'bars' },
      { path: '/stats', label: 'Thống kê chung', ready: true, icon: 'trend' },
    ],
  },
  {
    title: 'Tra cứu',
    icon: 'search',
    items: [
      { path: '/ships', label: 'Hồ sơ tàu', ready: true, icon: 'ship' },
      { path: '/ports', label: 'Hồ sơ cảng', ready: true, icon: 'anchor' },
      { path: '/agents', label: 'Hồ sơ đại lý', ready: true, icon: 'users' },
      { path: '/prospects', label: 'Tàu đi cảng khác', ready: true, icon: 'compass' },
      /* Sổ tàu của Cục Đăng kiểm (docs/16-vessel-registry/03-plan.md §7, R1). */
      { path: '/registry', label: 'Đội tàu VR', ready: true, icon: 'shield' },
      /* Mọi tàu hợp nhất của kho tàu thế giới (17.04 §24) — chủ dự án chốt 03/10/2026: đứng dưới VR. */
      { path: '/fleet', label: 'Đội tàu Thế giới', ready: true, icon: 'ship' },
    ],
  },
  {
    title: 'Danh mục',
    icon: 'folder',
    items: [
      {
        path: '/admin/terminals',
        ready: true,
        label: 'Cảng & bến',
        adminOnly: true,
        icon: 'anchor',
      },
      {
        path: '/admin/terminal-groups',
        ready: true,
        label: 'Nhóm cảng',
        adminOnly: true,
        icon: 'layers',
      },
      {
        path: '/admin/cargo-groups',
        ready: true,
        label: 'Nhóm hàng',
        adminOnly: true,
        icon: 'box',
      },
      { path: '/admin/agents', ready: true, label: 'Đại lý', adminOnly: true, icon: 'users' },
    ],
  },
  {
    /* Ba màn về danh mục CỦA CẢNG VỤ, tách khỏi danh mục nghiệp vụ của ta (15.03 §2.1): nhóm
       trên trả lời "ta phân cảng thế nào", nhóm này trả lời "nguồn vẽ vùng nước thế nào". Tách
       ra 14/09/2026 khi nhóm "Danh mục" chạm 7 mục — quá trần 6 của luật nhóm ngắn. Hai màn đồng
       bộ Đăng kiểm · kho tàu thế giới từng đứng đây, dời sang "Thu thập dữ liệu" 03/10/2026. */
    title: 'Danh mục nguồn',
    icon: 'compass',
    items: [
      {
        path: '/admin/berth-catalog',
        ready: true,
        label: 'Danh mục cầu bến',
        adminOnly: true,
        icon: 'anchor',
      },
      {
        path: '/admin/berth-coords',
        ready: true,
        label: 'Toạ độ bến',
        adminOnly: true,
        icon: 'anchor',
      },
      {
        path: '/admin/berth-links',
        ready: true,
        label: 'Liên kết danh mục',
        adminOnly: true,
        icon: 'anchor',
      },
    ],
  },
  {
    /* Ba màn "lượt cào / lượt đồng bộ" của BA nguồn ngoài, gom một nhóm (chủ dự án chốt 03/10/2026,
       docs/13-tasks/01-ui-chrome.md#61): trước đó Cảng vụ nằm ở "Hệ thống" dưới tên "Thu thập dữ
       liệu", hai nguồn kia ở "Danh mục nguồn". Tên nhóm lấy tên cũ của màn Cảng vụ, nên màn ấy đổi
       thành "Kế hoạch Cảng vụ". Thứ tự theo lúc nguồn vào hệ — agent tự quyết, bác thì tôi sửa. */
    title: 'Thu thập dữ liệu',
    icon: 'download',
    items: [
      {
        path: '/admin/scraping',
        ready: true,
        label: 'Kế hoạch Cảng vụ',
        adminOnly: true,
        icon: 'calendar',
      },
      {
        path: '/admin/registry',
        ready: true,
        label: 'Hồ sơ Đăng kiểm',
        adminOnly: true,
        icon: 'shield',
      },
      {
        path: '/admin/fleet',
        ready: true,
        label: 'Kho tàu thế giới',
        adminOnly: true,
        icon: 'ship',
      },
    ],
  },
  {
    title: 'Hệ thống',
    icon: 'sliders',
    items: [
      {
        path: '/admin/data-quality',
        ready: true,
        label: 'Chất lượng dữ liệu',
        adminOnly: true,
        icon: 'shield',
      },
      { path: '/admin/users', ready: true, label: 'Người dùng', adminOnly: true, icon: 'account' },
      { path: '/admin/api-keys', ready: true, label: 'Khoá API', adminOnly: true, icon: 'key' },
    ],
  },
];

/** Every screen of the navigation, flattened — what the route table walks. */
export function navItems(): readonly NavItem[] {
  return NAVIGATION.flatMap((section) => section.items);
}

/** The screens a reader of this role may open. */
export function visibleSections(role: 'admin' | 'viewer'): readonly NavSection[] {
  if (role === 'admin') return NAVIGATION;
  return NAVIGATION.map((section) => ({
    ...section,
    items: section.items.filter((item) => item.adminOnly !== true),
  })).filter((section) => section.items.length > 0);
}
